import { useState } from 'react';
import { Button, HStack, Input, Stack, Text } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import useApiKeysStore from '@/utils/use-api-keys';
import { ApiKey } from '@/lib/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  apiKey: ApiKey | null;
}

export default function RevokeConfirmDialog({ isOpen, onClose, apiKey }: Props) {
  const { workspaceId } = useParams();
  const revokeApiKey = useApiKeysStore((s) => s.revokeApiKey);
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function handleClose() {
    setConfirmation('');
    setSubmitting(false);
    onClose();
  }

  async function handleRevoke() {
    if (!apiKey || !workspaceId) return;
    setSubmitting(true);
    try {
      await revokeApiKey(workspaceId, apiKey.id);
      toaster.create({ type: 'success', title: 'API key revoked' });
      handleClose();
    } catch (e: any) {
      toaster.create({
        type: 'error',
        title: 'Could not revoke key',
        description: e?.response?.data?.error,
      });
      setSubmitting(false);
    }
  }

  const matches = apiKey ? confirmation === apiKey.name : false;

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(d) => (!d.open ? handleClose() : null)}>
      <DialogBackdrop />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Revoke API key</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={4}>
            <Text fontSize='sm'>
              Revoking this key will immediately stop any application using it. This can&apos;t be
              undone.
            </Text>
            {apiKey && (
              <Field label={`Type "${apiKey.name}" to confirm`}>
                <Input
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder={apiKey.name}
                  autoFocus
                />
              </Field>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <HStack>
            <Button variant='ghost' onClick={handleClose} type='button'>
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={handleRevoke}
              disabled={!matches || submitting}
              loading={submitting}
            >
              Revoke key
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
